// 'always' groups four-digit amounts too ("1.234,50" in es, where the default leaves "1234,50"). The
// ES2020 lib types `useGrouping` as boolean; engines that predate the string values coerce it to true.
const ALWAYS_GROUP = { useGrouping: 'always' } as unknown as Intl.NumberFormatOptions;

/**
 * Sub-unit amounts keep four decimals: a WhatsApp message costs about a cent, so "US$ 0.01" per order
 * would hide the very number the figure is there to show. Anything from one unit up keeps two.
 */
function fractionDigits(amount: number, minorUnits = 2): Intl.NumberFormatOptions {
  const small = amount !== 0 && Math.abs(amount) < 1;
  return { minimumFractionDigits: minorUnits, maximumFractionDigits: small ? Math.max(4, minorUnits) : minorUnits };
}

/** The currency's own minor units (CLP 0, UYU 2); 2 when the engine does not know the code. */
function minorUnitsOf(code: string, locale: string): number {
  try {
    return (
      new Intl.NumberFormat(locale, { style: 'currency', currency: code }).resolvedOptions().maximumFractionDigits ?? 2
    );
  } catch {
    return 2;
  }
}

/**
 * An amount in `currency` (ISO 4217), in the reader's language. An unknown or missing code still
 * prints the number — with the code in front when there is one — rather than throwing mid-render.
 */
export function formatMoney(amount: number, currency: string | null | undefined, locale: string): string {
  const code = currency?.trim().toUpperCase();
  if (code) {
    try {
      return new Intl.NumberFormat(locale, {
        ...ALWAYS_GROUP,
        ...fractionDigits(amount, minorUnitsOf(code, locale)),
        style: 'currency',
        currency: code,
      }).format(amount);
    } catch {
      return `${code} ${formatAmount(amount, locale)}`;
    }
  }
  return formatAmount(amount, locale);
}

/**
 * A US$ amount, always written "US$ …": the plain "$" Intl gives USD in some locales reads as the
 * brand's own peso, and the cost figures sit right next to local ones.
 */
export function formatUsd(amount: number, locale: string): string {
  return `US$ ${formatAmount(amount, locale)}`;
}

function formatAmount(amount: number, locale: string): string {
  return new Intl.NumberFormat(locale, { ...ALWAYS_GROUP, ...fractionDigits(amount) }).format(amount);
}
